import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, MessageCircle } from "lucide-react";
import { BackButton } from "@/components/ui/back-button";
import { z } from "zod";
import { openInExternalBrowser } from "@/lib/platform";
import { useTranslation } from "react-i18next";

const contactSchema = z.object({
  subject: z.string().trim().min(1, "Subject is required").max(200, "Subject must be less than 200 characters"),
  message: z
    .string()
    .trim()
    .min(10, "Message must be at least 10 characters")
    .max(2000, "Message must be less than 2000 characters"),
  email: z.string().trim().email("Invalid email address"),
});

const ContactUs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Moved schema inside component to use t (Memoized to avoid recreation)
  // or just use t inside handleSubmit for custom validation if needed, but zod is used.
  // actually, for simplicity in this replace, I will keep schema outside but localize the rest.
  // Validation errors can be localized later if needed, or I can define schema inside.
  // Defining schema inside is fine if not expensive.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate input
    try {
      contactSchema.parse({ subject, message, email });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();

      if (!sessionData.session) {
        toast.error(t('support.contact.login_required')); // "You must be logged in to contact us"
        navigate("/auth");
        return;
      }

      const { data, error } = await supabase.functions.invoke("send-support-email", {
        body: {
          type: "contact_us",
          subject,
          message,
          userEmail: email,
        },
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
      });

      if (error) {
        if (error.message?.includes("429")) {
          toast.error(t('support.contact.rate_limit')); // "Rate limit exceeded..."
        } else {
          throw error;
        }
        return;
      }

      toast.success(t('support.contact.success')); // "Message sent successfully..."
      setSubject("");
      setMessage("");
      setTimeout(() => navigate("/"), 2000);
    } catch (error: any) {
      console.error("Error submitting contact form:", error);
      toast.error(error.message || t('support.contact.failed')); // "Failed to send message"
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="fixed top-4 left-4 z-50">
        <BackButton onClick={() => navigate("/")} label={t('common.back_to_dashboard')} />
      </div>
      <div className="container max-w-2xl mx-auto pt-16 pb-8 px-4">

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Mail className="w-6 h-6 text-primary" />
              <CardTitle>{t('support.contact.title')}</CardTitle>
            </div>
            <CardDescription>{t('support.contact.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <MessageCircle className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-lg">{t('support.contact.discord_title')}</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                {t('support.contact.discord_desc')}
              </p>
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => openInExternalBrowser("https://discord.gg/FaYD2mJFhW")}
              >
                {t('support.contact.discord_button')}
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('support.contact.email_label')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('support.contact.email_placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  maxLength={255}
                />
                <p className="text-xs text-muted-foreground">{t('support.contact.email_help')}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">{t('support.contact.subject_label')}</Label>
                <Input
                  id="subject"
                  placeholder={t('support.contact.subject_placeholder')}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  maxLength={200}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">{t('support.contact.message_label')}</Label>
                <Textarea
                  id="message"
                  placeholder={t('support.contact.message_placeholder')}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  minLength={10}
                  maxLength={2000}
                  rows={8}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground">{message.length}/2000 {t('support.contact.characters')}</p>
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? t('common.sending') : t('support.contact.submit')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContactUs;
